// @givmo/app-charity — back-office enrollment vocabulary (CBO-IN3).
//
// Every word this feature renders about a record's state is SELECTED FROM THE BACKEND STATUS and
// lives here, in the `claim-vocab.ts` / `payment-vocab.ts` idiom. Two separate reasons, and both
// bind:
//
//   1. **A status label is a claim.** Composing one inline from screen state is how a surface ends up
//      telling an organization something the record does not say. Here the only input is the status
//      the wire returned, and a status this build does not recognize gets a neutral label rather than
//      the nearest familiar one.
//   2. **The app-wide copy rail polices this vocabulary by name.** Centralizing it means there is one
//      place to read when the rail's ruling changes, instead of a dozen inline strings.
//
// ══ THE SUBSTITUTION TABLE THIS MODULE OBEYS ═══════════════════════════════════════════════════
//
// The suite's own vocabulary law governs every string below. The substitutions that bite this
// surface, and what is written instead:
//
//   • the *confirm-a-fact* roots (the "we checked it" and "you swore to it" families) → "recorded",
//     "your organization's own account of", "source checked on <date>";
//   • the *trail* root → "record event log";
//   • the roots that name a document ASSERTING a fact, and the one that names a proof-of-payment
//     document → **onboarding record**, which is what this flow produces and the only name it is
//     ever given in rendered copy;
//   • the finished-and-nothing-left words, applied to the BOOKS → setup-scoped copy only. "Setup
//     complete" is true and sanctioned, because setup is a thing that finishes. The same words
//     applied to what the books CONTAIN are banned outright, and appear nowhere.
//
// ⚠ NO FEE OR CONSIDERATION COPY EXISTS HERE, IN EITHER DIRECTION. Suite consideration copy is on
// hold, and a held slot RENDERS NOTHING — not a placeholder, not a reassurance, not a dash. A surface
// that said the module costs nothing would have said something about consideration just as surely as
// one that named an amount.
//
// ⚠ NOTHING HERE CONDITIONS MONEY ON ENROLLMENT. Any sentence making this act a precondition of
// grants arriving is the same false shape the D-CP-11 ruling banned for claiming, and is false for
// the same reason: grants reach a nonprofit whether or not it has claimed, and whether or not it has
// been set up in the back office. Setting up changes what the organization can SEE and RECORD about
// its own books — never whether the money arrives. The banned shapes are enumerated in
// `enrollment-copy-law.test.ts`, which is the only file in this feature permitted to spell them.
//
// ⚠ NO SPEED CLAIMS, HEDGED OR OTHERWISE. Approval fires the moment a precondition set is satisfied
// and does not when it is not, so any promise about WHEN would be a promise about preconditions this
// surface neither owns nor can see.

import type { EnrollmentAction, EnrollmentStatus } from './enrollment-decode';

/** SaaS Badge tones (the subset this surface uses). */
export type EnrollmentBadgeTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'brand';

/** What one status is called, what it means, and how it is toned. */
export interface EnrollmentStatusVocab {
  label: string;
  description: string;
  tone: EnrollmentBadgeTone;
}

/**
 * The complete status vocabulary.
 *
 * Read the descriptions as a set: each says what HAS happened and, where there is one, what happens
 * next. None of them says how long anything takes, and none promises an outcome.
 */
const STATUS_VOCAB: Record<EnrollmentStatus, EnrollmentStatusVocab> = {
  draft: {
    label: 'Draft',
    description:
      'Not submitted yet. You can still change these details, and nothing has been created for your organization.',
    tone: 'neutral',
  },
  submitted: {
    label: 'Submitted',
    description:
      'Your organization has asked for its books to be set up. What happens next is recorded on this page.',
    tone: 'info',
  },
  approved: {
    label: 'Approved',
    description:
      'Approved. Your organization’s books are being created; the outcome is recorded on this page.',
    tone: 'info',
  },
  provisioned: {
    label: 'Setup complete',
    description:
      'Your organization’s books exist. Setup is done — bringing in your history is a separate step you can work through whenever you are ready.',
    tone: 'success',
  },
  rejected: {
    label: 'Not approved',
    description: 'This request was not approved. The reason recorded for it is shown below.',
    tone: 'warning',
  },
  withdrawn: {
    label: 'Withdrawn',
    description:
      'This request was withdrawn. You can start a new one whenever your organization is ready.',
    tone: 'neutral',
  },
};

/**
 * The label for a status this build does not recognize.
 *
 * ⚠ Deliberately says nothing about what the record IS. A newer backend state rendered as the
 * nearest familiar label would be a confident wrong answer, and the states this surface could get
 * wrong are exactly the ones an organization would act on.
 */
const UNKNOWN_STATUS_VOCAB: EnrollmentStatusVocab = {
  label: 'Recorded',
  description:
    'This record is in a state this version of the console doesn’t recognize yet. Nothing is wrong with the record itself.',
  tone: 'neutral',
};

/** The vocabulary for one status, tolerant of `null` and of a state this build does not know. */
export function enrollmentStatusVocab(status: EnrollmentStatus | null): EnrollmentStatusVocab {
  return status === null ? UNKNOWN_STATUS_VOCAB : STATUS_VOCAB[status];
}

/**
 * The event-log line for one recorded action.
 *
 * Past tense throughout, because every one of these is something that already happened — the log is
 * a record of events, not a progress indicator.
 */
const ACTION_LABEL: Record<EnrollmentAction, string> = {
  created: 'Record started',
  updated: 'Details changed',
  submitted: 'Submitted for setup',
  approved: 'Approved',
  provisioned: 'Books created',
  rejected: 'Not approved',
  withdrawn: 'Withdrawn',
  store_copy_recorded: 'Record copied to its second home',
  provision_redriven: 'Setup re-run',
  // The intake doors (CBO-IN4). ⚠ "Import refused" is a REAL, RECORDED outcome and reads as plainly
  // as the ones above — a refused attempt happened, and softening it into "import didn't finish"
  // would describe an absence where the log holds an event.
  backfill_choice_recorded: 'History method recorded',
  entity_map_recorded: 'Account map recorded',
  import_run_started: 'Import started',
  import_run_bound: 'Import recorded',
  import_run_refused: 'Import refused',
};

/** The label for one event-log action. An action this build does not know renders neutrally. */
export function enrollmentActionLabel(action: EnrollmentAction | null): string {
  return action === null ? 'Recorded' : ACTION_LABEL[action];
}

/**
 * The twelve month names, in calendar order, for the fiscal-year control.
 *
 * ⚠ HARD-CODED ENGLISH, NOT `toLocaleDateString`. Every other date on this surface is formatted in
 * the viewer's locale, and this one deliberately is not: the wire value is a MONTH NUMBER, and a
 * locale-derived name would have to be produced by constructing a throwaway `Date` — which drags in
 * the viewer's time zone and can render month 1 as "December" for anyone west of UTC. A twelve-entry
 * table has no such failure mode. The console is English-only today; when it stops being, this table
 * is where a translation lands, and it will still not be a `Date`.
 */
export const FISCAL_YEAR_MONTHS: readonly { value: number; label: string }[] = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

/**
 * The month a record settles to when nobody chooses one.
 *
 * The backend's own default, restated here because the control must PRESELECT it to be usable —
 * and preselecting a value the record does not hold is only honest while the two agree. If the
 * backend's settle-at-submission month ever moves, this is the constant that has to move with it.
 */
export const DEFAULT_FISCAL_YEAR_END_MONTH = 12;

/** One month's name. A month this build cannot read renders as an empty string, never a guess. */
export function fiscalYearMonthLabel(month: number | null): string {
  return FISCAL_YEAR_MONTHS.find((entry) => entry.value === month)?.label ?? '';
}

/** The page and section headings, in one place so the router, the shell and the pages agree. */
export const ENROLLMENT_COPY = {
  /** The `activeTitle` entry and the landing page's heading. */
  pageTitle: 'Organization setup',
  pageSubtitle:
    'Set your organization up in the back office so it has its own books to record against.',
  /** The zero-state, for an organization with no record yet. */
  startTitle: 'Set up your organization',
  startBody:
    'Setting up gives your organization its own books in the back office. You can start now and fill in the details as you go.',
  startAction: 'Start setup',
  /** The draft form. */
  detailsTitle: 'Your organization’s details',
  detailsBody:
    'Your organization’s own account of how it is registered. You can change any of it while this record is a draft.',
  legalNameLabel: 'Legal name',
  legalNameHint:
    'The name your organization is registered under, if it differs from its public one.',
  einLabel: 'EIN',
  jurisdictionLabel: 'State or jurisdiction of registration',
  /**
   * The fiscal-year convention (CBO-IN4).
   *
   * ⚠ PLAIN LANGUAGE, NO JARGON, AND NO INSTRUCTION TO GO LOOK IT UP. "Fiscal year end month" is
   * what an accountant calls it; what a person running a nonprofit knows is the month its financial
   * year finishes in, which is on every Form 990 it has ever filed. The hint says where the answer
   * already exists rather than asking anyone to derive one.
   *
   * ⚠ AND IT SAYS WHY THE MOMENT MATTERS, without a deadline word. This is the one detail on the
   * form that cannot be changed after the books are created — not because the console stops
   * offering it, but because it decides what a year-end MEANS on those books. Copy that treated it
   * like the other three fields would be quietly wrong about the one that is different.
   */
  fiscalYearLabel: 'Month your financial year ends',
  fiscalYearHint:
    'The month your organization’s financial year finishes in — the same one on its Form 990. Most organizations end in December, and that is what applies unless you change it. This one is worth getting right before you finish setup: it sets what a year-end means on your books, and it stays as it was created.',
  saveAction: 'Save details',
  /**
   * The submit action.
   *
   * ⚠ "Finish setup" is scoped to SETUP on purpose. It finishes creating the organization's books; it
   * does not mean the books are complete, and nothing on this surface treats a finished setup as a
   * statement about how much history has been brought in.
   */
  submitAction: 'Finish setup',
  /**
   * ⚠ Says what THIS CONSOLE does, not what the record forbids. Editing stays possible on the
   * backend for a little longer than the console offers it (see `isEditable`), so "you won't be able
   * to change this" would state a console choice as a fact about the record. What is true, and what
   * a person actually needs to know before pressing it, is which door stays open afterwards.
   */
  submitHint:
    'This asks for your organization’s books to be created. Afterwards the details above aren’t editable here — if something needs changing, withdraw the request and start again.',
  withdrawAction: 'Withdraw request',
  /**
   * The way into the intake doors (CBO-IN4), offered only once the books exist.
   *
   * ⚠ NO COUNT, NO SEQUENCE, NO "NEXT". Bringing history in is open-ended and has no end state, so
   * copy that framed it as a remaining step would be describing a finish line that does not exist.
   * What it says is what the surface is for.
   */
  backfillBody:
    'Your organization’s books exist and are empty of what came before them. That gets added a piece at a time, whenever you are ready.',
  backfillAction: 'Bring in your history',
  /** The event log. */
  logTitle: 'Record event log',
  /**
   * The LISTING's 404 — the one place the gate inference is sound.
   *
   * A 404 on the organization's own collection can only mean a gate is off: the collection exists
   * for every organization, so there is no "no such thing" reading available. The per-record copy
   * below cannot make the same inference and does not try.
   */
  unavailableTitle: 'Setup isn’t available here yet',
  unavailableBody:
    'This part of the console isn’t switched on in this environment. Nothing your organization has recorded is affected.',
  /**
   * ONE record's 404 — deliberately true whichever of the two things happened.
   *
   * ⚠ The backend answers a dark gate and a record outside the caller's scope with byte-identical
   * bodies, so the client genuinely cannot tell them apart — and a stale bookmark, or switching
   * organizations while sitting on a record page, reaches this with the module fully ON. Copy that
   * named either cause would be a coin flip stated as fact. Naming both, without choosing, is the
   * only honest sentence available, and it keeps the non-disclosure property the backend built.
   */
  recordUnavailableTitle: 'This record isn’t available here',
  recordUnavailableBody:
    'It may belong to a different organization, or this part of the console may not be switched on in this environment. Nothing your organization has recorded is affected.',
  /**
   * The 409 a second live record is refused with.
   *
   * ⚠ Says "that one", not "below". A 409 means the listing on screen was stale, so the screen
   * refetches — and the record that arrives REPLACES the start card rather than appearing under it.
   * Copy that named a position would be describing a layout the refresh has just changed.
   */
  alreadyEnrolledBody:
    'Your organization already has a record open. Open that one rather than starting another.',
} as const;
